import { useEffect, useState } from "react";
import { approveInvoice, createInvoice, issueInvoice, listInvoices } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  title: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  status: string;
  dueDate: string | null;
  currency: string;
  version: number;
  taxAmount: number;
}

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "text-gray-400 bg-gray-800",
  APPROVED: "text-blue-400 bg-blue-900/30",
  ISSUED: "text-indigo-400 bg-indigo-900/30",
  PARTIALLY_PAID: "text-yellow-400 bg-yellow-900/30",
  PAID: "text-green-400 bg-green-900/30",
  OVERDUE: "text-red-400 bg-red-900/30",
  CANCELLED: "text-gray-600 bg-gray-900",
};

function currency(n: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinanceInvoicesPage() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    title: "",
    unitPrice: "",
    quantity: "1",
    taxType: "GST",
    cgstRate: "9",
    sgstRate: "9",
    dueDate: "",
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    setLoading(true);
    void listInvoices(token, { status: statusFilter || undefined }).then((d) => {
      setInvoices((d as { items: Invoice[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, [statusFilter]);

  const create = async () => {
    if (!form.clientId || !form.title || !form.unitPrice) return;
    setCreating(true);
    const qty = parseFloat(form.quantity) || 1;
    const price = parseFloat(form.unitPrice) || 0;
    await createInvoice(token, {
      clientId: form.clientId,
      title: form.title,
      lineItems: [
        {
          id: "item-1",
          description: form.title,
          quantity: qty,
          unitPrice: price,
          amount: qty * price,
        },
      ],
      taxType: form.taxType,
      cgstRate: parseFloat(form.cgstRate) || 0,
      sgstRate: parseFloat(form.sgstRate) || 0,
      dueDate: form.dueDate || undefined,
    });
    setShowForm(false);
    setCreating(false);
    reload();
  };

  const approve = async (inv: Invoice) => {
    if (!window.confirm(`Approve invoice ${inv.invoiceNumber}?`)) return;
    await approveInvoice(token, inv.id, inv.version);
    reload();
  };

  const issue = async (inv: Invoice) => {
    if (!window.confirm(`Issue invoice ${inv.invoiceNumber} to client? This cannot be undone.`))
      return;
    await issueInvoice(token, inv.id, inv.version);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="">All statuses</option>
              {[
                "DRAFT",
                "APPROVED",
                "ISSUED",
                "PARTIALLY_PAID",
                "PAID",
                "OVERDUE",
                "CANCELLED",
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowForm(!showForm)}
              className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
            >
              {showForm ? "Cancel" : "New Invoice"}
            </button>
          </div>
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <h2 className="text-sm font-medium text-gray-300">New Invoice</h2>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                placeholder="Client ID"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Invoice Title"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.unitPrice}
                onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
                placeholder="Unit Price (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                placeholder="Quantity"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <select
                value={form.taxType}
                onChange={(e) => setForm((f) => ({ ...f, taxType: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                <option value="NONE">No Tax</option>
                <option value="GST">GST (CGST + SGST)</option>
                <option value="IGST">IGST</option>
              </select>
              <input
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                type="date"
                placeholder="Due Date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
            </div>
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Invoice"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {invoices.length === 0 && <p className="text-gray-400">No invoices yet.</p>}
            {invoices.map((inv) => (
              <div key={inv.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{inv.invoiceNumber}</span>
                    <span className="ml-3 font-medium text-white">{inv.title}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">{currency(inv.total)}</p>
                      {inv.amountDue > 0 && (
                        <p className="text-xs text-red-400">Due: {currency(inv.amountDue)}</p>
                      )}
                    </div>
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[inv.status] ?? "bg-gray-800 text-gray-400"}`}
                    >
                      {inv.status}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  {inv.status === "DRAFT" && (
                    <button
                      onClick={() => void approve(inv)}
                      className="rounded bg-blue-900/50 px-2 py-1 text-xs text-blue-300 hover:bg-blue-900"
                    >
                      Approve
                    </button>
                  )}
                  {inv.status === "APPROVED" && (
                    <button
                      onClick={() => void issue(inv)}
                      className="rounded bg-green-900/50 px-2 py-1 text-xs text-green-300 hover:bg-green-900"
                    >
                      Issue to Client
                    </button>
                  )}
                  {inv.dueDate && (
                    <span className="text-xs text-gray-500">
                      Due: {new Date(inv.dueDate).toLocaleDateString()}
                    </span>
                  )}
                  {inv.taxAmount > 0 && (
                    <span className="text-xs text-gray-500">GST: {currency(inv.taxAmount)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
